import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "./useAuth";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePageTitle } from "../../hooks/usePageTitle";
import { AppHeader } from "../../components/AppHeader";
import { AppFooter } from "../../components/AppFooter";
import { ownerApiFetchPublic } from "../../utils/ownerApi";
import { API_BASE } from "../../utils/api";

export function SignupPage() {
  usePageTitle("회원가입");
  const navigate = useNavigate();
  const m = useIsMobile();
  const { signup } = useAuth();

  const [role, setRole] = useState<"broker" | "owner">("broker");

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    passwordConfirm: "",
    office_name: "",
    representative_name: "",
    business_number: "",
    license_number: "",
    region_sido: "",
    region_sigungu: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // 사업자등록번호 진위확인 상태
  const [bizVerified, setBizVerified] = useState<boolean | null>(null);
  const [bizVerifyMsg, setBizVerifyMsg] = useState("");
  const [bizVerifying, setBizVerifying] = useState(false);

  const update = (key: string, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // 사업자등록번호 자동 포맷 (000-00-00000) + 검증 초기화
  function formatBizNo(raw: string): string {
    const d = raw.replace(/\D/g, "").slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
  }

  function updateBizNo(raw: string) {
    update("business_number", formatBizNo(raw));
    setBizVerified(null);
    setBizVerifyMsg("");
  }

  async function handleBizVerify() {
    const bizNo = form.business_number.replace(/\D/g, "");
    if (bizNo.length !== 10) {
      setBizVerified(false);
      setBizVerifyMsg("사업자등록번호 10자리를 입력해주세요");
      return;
    }
    setBizVerifying(true);
    setBizVerified(null);
    setBizVerifyMsg("");
    try {
      const res = await fetch(`${API_BASE}/api/verify/business`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bizNo }),
      });
      const data = await res.json() as any;
      if (data.valid) {
        setBizVerified(true);
        setBizVerifyMsg("영업 중인 사업자입니다");
      } else {
        setBizVerified(false);
        setBizVerifyMsg(data.reason || "확인 실패");
      }
    } catch {
      setBizVerified(false);
      setBizVerifyMsg("네트워크 오류. 잠시 후 다시 시도해주세요.");
    } finally {
      setBizVerifying(false);
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (form.password !== form.passwordConfirm) {
      setError("비밀번호가 일치하지 않습니다");
      return;
    }
    if (form.password.length < 6) {
      setError("비밀번호는 6자 이상이어야 합니다");
      return;
    }

    setLoading(true);
    try {
      if (role === "owner") {
        // 땅주인 회원가입
        const data = await ownerApiFetchPublic("/api/owner/signup", {
          method: "POST",
          body: JSON.stringify({
            name: form.name,
            phone: form.phone.replace(/\D/g, ""),
            email: form.email.trim() || undefined,
            password: form.password,
          }),
        });
        localStorage.setItem("owner_token", data.token);
        localStorage.setItem("owner_info", JSON.stringify(data.owner));
        navigate("/owner/register", { replace: true });
      } else {
        // 중개사 회원가입
        await signup({
          name: form.name,
          phone: form.phone.replace(/\D/g, ""),
          email: form.email.trim() || undefined,
          password: form.password,
          office_name: form.office_name,
          representative_name: form.representative_name || undefined,
          business_number: form.business_number || undefined,
          license_number: form.license_number,
          region_sido: form.region_sido,
          region_sigungu: form.region_sigungu,
        });
        navigate("/dashboard", { replace: true });
      }
    } catch (err: any) {
      setError(err.message || "회원가입에 실패했습니다");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <AppHeader />
      <div style={{ ...styles.container, padding: m ? "20px 16px 40px" : "40px 32px 60px" }}>

        <div style={styles.pageHeader}>
          <h1 style={styles.pageTitle}>
            <span style={{ color: "var(--lt-gold)" }}>땅</span>신뢰 회원가입
          </h1>
        </div>

        {/* 역할 탭 선택 */}
        <div style={{ display: "flex", gap: 0, marginBottom: 24, borderRadius: "var(--radius-md)", overflow: "hidden", border: "1px solid var(--border-medium)" }}>
          <button
            type="button"
            onClick={() => setRole("broker")}
            style={{
              flex: 1,
              padding: "12px 8px",
              border: "none",
              cursor: "pointer",
              fontWeight: 700,
              fontSize: 14,
              transition: "var(--transition-fast)",
              background: role === "broker" ? "linear-gradient(135deg, #B8972E, #D4AF37)" : "var(--bg-card)",
              color: role === "broker" ? "#0F172A" : "var(--text-muted)",
            }}
          >
            {"🏢"} 중개사로 가입
          </button>
          <button
            type="button"
            onClick={() => setRole("owner")}
            style={{
              flex: 1,
              padding: "12px 8px",
              border: "none",
              borderLeft: "1px solid var(--border-medium)",
              cursor: "pointer",
              fontWeight: 700,
              fontSize: 14,
              transition: "var(--transition-fast)",
              background: role === "owner" ? "linear-gradient(135deg, #B8972E, #D4AF37)" : "var(--bg-card)",
              color: role === "owner" ? "#0F172A" : "var(--text-muted)",
            }}
          >
            {"🌱"} 땅주인으로 가입
          </button>
        </div>

        {role === "owner" && (
          <div style={{ background: "rgba(212,168,83,0.06)", border: "1px solid rgba(212,168,83,0.22)", borderLeft: "3px solid #D4A853", borderRadius: "var(--radius-md)", padding: "14px 18px", marginBottom: 16 }}>
            <p style={{ margin: 0, fontSize: 13, color: "#D4A853", fontWeight: 600 }}>{"🌱"} 땅주인 전용 가입</p>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-muted)", lineHeight: 1.6 }}>공인중개사 자격증 없이 가입 가능합니다. 가입 후 내 땅을 직접 등록하면 인증된 중개사에게 노출됩니다.</p>
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>

          {/* ── 1. 기본 정보 ── */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>기본 정보</h3>
            <div style={styles.sectionBody}>
              <div style={{ display: "grid", gridTemplateColumns: m ? "1fr" : "1fr 1fr", gap: 14 }}>
                <Field label="이름" value={form.name} onChange={(v) => update("name", v)} placeholder="홍길동" required />
                <Field label="휴대폰 번호" value={form.phone} onChange={(v) => update("phone", v)} placeholder="010-0000-0000" type="tel" required />
                <Field label="이메일 (문의 알림 수신용, 선택)" value={form.email} onChange={(v) => update("email", v)} placeholder="example@email.com" type="email" />
                <div />
                <Field label="비밀번호" value={form.password} onChange={(v) => update("password", v)} placeholder="6자 이상" type="password" required />
                <Field label="비밀번호 확인" value={form.passwordConfirm} onChange={(v) => update("passwordConfirm", v)} placeholder="비밀번호 다시 입력" type="password" required />
              </div>
            </div>
          </div>

          {/* ── 2. 중개사 정보 (중개사 전용) ── */}
          {role === "broker" && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>중개사 정보</h3>
              <div style={styles.sectionBody}>
                <div style={{ display: "grid", gridTemplateColumns: m ? "1fr" : "1fr 1fr", gap: 14 }}>
                  <Field label="소속 중개사무소명" value={form.office_name} onChange={(v) => update("office_name", v)} placeholder="예: 땅신뢰 공인중개사사무소" required={role === "broker"} />
                  <Field label="자격증번호" value={form.license_number} onChange={(v) => update("license_number", v)} placeholder="예: 2024-서울-12345" required={role === "broker"} />
                  <Field label="대표자명 (선택)" value={form.representative_name} onChange={(v) => update("representative_name", v)} placeholder="예: 홍길동" />
                  <div />
                </div>

                {/* 사업자등록번호 — 국세청 진위확인 */}
                <div style={{ marginTop: 14, display: "flex", flexDirection: "column" as const, gap: 6 }}>
                  <label style={fieldStyles.label}>
                    사업자등록번호
                    <span style={{ color: "var(--text-muted)", fontSize: 12, fontWeight: 400, marginLeft: 6 }}>(입력 시 진위확인 필수)</span>
                  </label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      type="text"
                      placeholder="123-45-67890"
                      value={form.business_number}
                      onChange={(e) => updateBizNo(e.target.value)}
                      style={{ ...fieldStyles.input, flex: 1 }}
                      maxLength={12}
                    />
                    <button
                      type="button"
                      onClick={handleBizVerify}
                      disabled={bizVerifying || form.business_number.replace(/\D/g, "").length !== 10}
                      style={{
                        padding: "0 20px",
                        borderRadius: "var(--radius-md)",
                        border: "1px solid rgba(212,168,83,0.45)",
                        background: bizVerified === true
                          ? "rgba(16,185,129,0.12)"
                          : "rgba(212,168,83,0.1)",
                        color: bizVerified === true ? "#10B981" : "#D4A853",
                        fontSize: 14, fontWeight: 700,
                        cursor: (bizVerifying || form.business_number.replace(/\D/g, "").length !== 10) ? "not-allowed" : "pointer",
                        opacity: form.business_number.replace(/\D/g, "").length !== 10 ? 0.45 : 1,
                        whiteSpace: "nowrap" as const,
                        flexShrink: 0,
                        minWidth: 80,
                        transition: "background 0.2s, color 0.2s",
                      }}
                    >
                      {bizVerifying ? "확인 중..." : bizVerified === true ? "\u2705 확인됨" : "확인"}
                    </button>
                  </div>
                  {bizVerifyMsg && (
                    <div style={{ fontSize: 13, color: bizVerified ? "#10B981" : "#EF4444", display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                      {bizVerified ? "\u2705" : "\u274C"} {bizVerifyMsg}
                    </div>
                  )}
                  {form.business_number.replace(/\D/g, "").length === 10 && bizVerified === null && !bizVerifying && (
                    <p style={{ fontSize: 12, color: "#FBBF24", margin: "2px 0 0" }}>
                      {"\u261D"} "확인" 버튼을 눌러 진위확인을 완료해주세요
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── 3. 주 활동 지역 (중개사 전용) ── */}
          {role === "broker" && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>주 활동 지역</h3>
              <div style={styles.sectionBody}>
                <div style={{ display: "grid", gridTemplateColumns: m ? "1fr" : "1fr 1fr", gap: 14 }}>
                  <Field label="시/도" value={form.region_sido} onChange={(v) => update("region_sido", v)} placeholder="예: 경기도" required={role === "broker"} />
                  <Field label="시/군/구" value={form.region_sigungu} onChange={(v) => update("region_sigungu", v)} placeholder="예: 용인시 처인구" required={role === "broker"} />
                </div>
              </div>
            </div>
          )}

          {error && <p style={styles.error}>{error}</p>}

          {role === "broker" && form.business_number.replace(/\D/g, "").length === 10 && bizVerified !== true && (
            <p style={{ fontSize: 13, color: "#FBBF24", margin: "0", textAlign: "center" as const }}>
              {"\u26A0\uFE0F"} 사업자등록번호 진위확인을 완료해야 가입할 수 있습니다
            </p>
          )}

          <button
            type="submit"
            disabled={loading || (role === "broker" && form.business_number.replace(/\D/g, "").length === 10 && bizVerified !== true)}
            style={{
              ...styles.btn,
              opacity: (loading || (role === "broker" && form.business_number.replace(/\D/g, "").length === 10 && bizVerified !== true)) ? 0.5 : 1,
              cursor: (loading || (role === "broker" && form.business_number.replace(/\D/g, "").length === 10 && bizVerified !== true)) ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "가입 중..." : role === "owner" ? "땅주인으로 가입하기" : "중개사로 가입하기"}
          </button>
        </form>

        <p style={styles.linkText}>
          이미 계정이 있으신가요?{" "}
          <Link to="/login" style={styles.link}>
            로그인
          </Link>
        </p>
      </div>
      <AppFooter />
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <div style={fieldStyles.field}>
      <label style={fieldStyles.label}>{label}</label>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={fieldStyles.input}
        required={required}
      />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column" as const,
    background: "var(--bg-primary)",
  },
  container: {
    flex: 1,
    maxWidth: 1200,
    margin: "0 auto",
    width: "100%",
  },
  pageHeader: {
    padding: "28px 0 4px",
    borderBottom: "1px solid var(--border-light)",
    marginBottom: 28,
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: 800,
    color: "var(--text-primary)",
    margin: "0 0 6px",
    letterSpacing: "-0.3px",
  },
  pageDesc: {
    fontSize: 14,
    color: "var(--text-muted)",
    margin: 0,
  },
  form: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 20,
  },
  section: {
    background: "var(--bg-card)",
    border: "1px solid var(--border-light)",
    borderLeft: "3px solid var(--lt-gold)",
    borderRadius: "var(--radius-xl)",
    overflow: "hidden",
    boxShadow: "var(--shadow-sm)",
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--lt-gold)",
    margin: 0,
    padding: "12px 20px",
    borderBottom: "1px solid var(--border-light)",
    letterSpacing: "0.8px",
    textTransform: "uppercase" as const,
    background: "rgba(212,168,83,0.04)",
  },
  sectionBody: {
    padding: 20,
  },
  error: {
    color: "var(--lt-red)",
    fontSize: 13,
    margin: 0,
    padding: "8px 12px",
    background: "rgba(239,68,68,0.08)",
    borderRadius: "var(--radius-md)",
  },
  btn: {
    padding: "14px",
    borderRadius: "var(--radius-md)",
    border: "none",
    background: "linear-gradient(135deg, #B8972E, #D4AF37)",
    color: "#0F172A",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    transition: "var(--transition-fast)",
    marginTop: 4,
    letterSpacing: "0.3px",
  },
  linkText: {
    textAlign: "center" as const,
    fontSize: 14,
    color: "var(--text-muted)",
    marginTop: 24,
  },
  link: {
    color: "var(--lt-gold)",
    textDecoration: "none",
    fontWeight: 600,
  },
};

const fieldStyles: Record<string, React.CSSProperties> = {
  field: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 6,
  },
  label: {
    fontSize: 14,
    fontWeight: 500,
    color: "var(--text-secondary)",
  },
  input: {
    padding: "12px 14px",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border-medium)",
    background: "var(--bg-secondary)",
    color: "var(--text-primary)",
    fontSize: 16,
    outline: "none",
    transition: "var(--transition-fast)",
    width: "100%",
    boxSizing: "border-box" as const,
  },
};
